import React, { useEffect, useState, useRef } from "react";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import { ColumnsType, ColumnType } from "antd/lib/table";
import { Button, Col, Input, InputRef, Space } from "antd";
import DataTable from "../components/DataTable";
import DigitalKeyOwnerDetail from "../components/DigitalKeyOwnerDetail";

import { ControlDoorType } from "../pages/DigitalKey";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";

type DataIndex = keyof Types.CarSharerDto;

function RentDigitalKey({
    refresh,
    setRefresh,
    onControlDoor,
    onAddCarOwner,
    onDeleteCarOwner,
}: {
    refresh: boolean;
    setRefresh: (refresh: boolean) => void;
    onControlDoor: (doorControl: ControlDoorType) => void;
    onAddCarOwner: () => void;
    onDeleteCarOwner: (carOwnerId: number) => void;
}) {
    const [shareCars, setShareCars] = useState<Array<Types.CarSharerDto>>([]);

    useEffect(() => {
        requestGetShareCars();
    }, []);

    const requestAxiosGetShareCars = async () => {
        const response = await utilAxiosWithAuth().get(Request.CAR_SHARERS_URL, { params: { shareType: "RENT" } });
        return response.data;
    };

    const {
        loading: loadingGetShareCars,
        error: errorGetShareCars,
        data: resultGetShareCars,
        execute: requestGetShareCars,
    } = useAsyncAxios(requestAxiosGetShareCars);

    useEffect(() => {
        if (resultGetShareCars === null) return;
        console.log("resultGetShareCars", resultGetShareCars);
        setShareCars(resultGetShareCars.carSharers);
    }, [resultGetShareCars]);

    useEffect(() => {
        if (errorGetShareCars === null) return;

        console.log("errorGetShareCars", errorGetShareCars);
    }, [errorGetShareCars]);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (
        dataIndex: DataIndex,
        subCarOwnerIndex?: keyof Types.CarOwnerDto,
        subCarIndex?: keyof Types.CarDto,
        subUserIndex?: keyof Types.UserDto
    ): ColumnType<Types.CarSharerDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) => {
            let fieldData = record[dataIndex];
            if (subCarOwnerIndex !== undefined) {
                const subFieldCarOwnerData = fieldData as Types.CarOwnerDto;
                const subFieldCarData = subFieldCarOwnerData[subCarOwnerIndex] as Types.CarDto;
                if (subCarIndex !== undefined) fieldData = subFieldCarData[subCarIndex] as string | number | boolean;
            }

            if (subUserIndex !== undefined) {
                const subFieldData = fieldData as Types.UserDto;
                fieldData = subFieldData[subUserIndex] as string | number | boolean;
            }

            if (fieldData === null || fieldData === undefined) return false;

            return fieldData
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase());
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.CarSharerDto> = [
        {
            align: "center",
            title: "번호",
            dataIndex: "num",
            key: "carSharerId",
            render: (item, record, index) => <>{shareCars && shareCars.indexOf(record) + 1}</>,
        },
        {
            align: "center",
            title: "등록일",
            dataIndex: "createdDate",
            key: "createdDate",
            sorter: (a, b, sortOrder) => Utils.sortString(a.createdDate.valueOf(), b.createdDate.valueOf(), sortOrder),
            render: (_, row) => {
                if (row.createdDate) {
                    return Utils.utcToLocalTime(row.createdDate.valueOf(), Common.FORMAT_SHORT_DATE_TIME);
                } else return String.dash;
            },
        },
        {
            align: "center",
            title: "차량번호",
            dataIndex: ["carOwner", "car", "carNumber"],
            key: "carNumber",
            sorter: (a, b, sortOrder) => Utils.sortString(a.carOwner.car.carNumber, b.carOwner.car.carNumber, sortOrder),
            ...getColumnSearchProps("carOwner", "car", "carNumber"),
        },
        {
            align: "center",
            title: "타입",
            dataIndex: ["carOwner", "car", "carType", "typeName"],
            key: "type",
            sorter: (a, b, sortOrder) => Utils.sortString(a.carOwner?.car?.carType?.typeName, b.carOwner?.car?.carType?.typeName, sortOrder),
            render: (_, row) => {
                return row.carOwner?.car?.carType?.typeName ? row.carOwner?.car?.carType?.typeName : String.dash;
            },
        },
        {
            align: "center",
            title: "사용자",
            dataIndex: ["user", "name"],
            key: "name",
            sorter: (a, b, sortOrder) => Utils.sortString(a.user.name, b.user.name, sortOrder),
            render: (_, row) => {
                return row.user?.name ? row.user?.name : String.dash;
            },
            ...getColumnSearchProps("user", undefined, undefined, "name"),
        },
        {
            align: "center",
            title: "전화번호",
            dataIndex: ["user", "phone"],
            key: "name",
            sorter: (a, b, sortOrder) => Utils.sortString(a.user.phone, b.user.phone, sortOrder),
            render: (_, row) => {
                return row.user?.phone ? row.user?.phone : String.dash;
            },
        },
        {
            align: "center",
            title: "배터리",
            dataIndex: ["carOwner", "car", "subBatteryStatus"],
            key: "subBatteryStatus",
            sorter: (a, b, sortOrder) => Utils.sortString(a.carOwner?.car.subBatteryStatus, b.carOwner?.car.subBatteryStatus, sortOrder),
            render: (_, row) => {
                if (row.carOwner?.car?.subBatteryStatus) {
                    switch (row.carOwner?.car?.subBatteryStatus) {
                        case "NORMAL":
                            return "정상";
                        case "LOW":
                            return "낮음";
                        case "VERY_LOW":
                            return "위험";
                        default:
                            return String.dash;
                    }
                } else return String.dash;
            },
        },
        {
            align: "center",
            title: "차량도어",
            dataIndex: ["carOwner", "car", "doorStatus"],
            key: "doorStatus",
            sorter: (a, b, sortOrder) => Utils.sortString(a.carOwner?.car.doorStatus, b.carOwner?.car.doorStatus, sortOrder),
            render: (_, row) => {
                if (row.carOwner?.car?.doorStatus) {
                    switch (row.carOwner?.car?.doorStatus) {
                        case "CLOSED":
                            return "닫힘";
                        case "OPEN":
                            return "열림";
                        case "FAIL_NOT_P":
                            return "기어P오류";
                        case "KEYOFF_NOT_P":
                            return "KeyOff 오류";
                        default:
                            return String.dash;
                    }
                } else return String.dash;
            },
        },
        {
            align: "center",
            title: "차량도어 컨트롤",
            key: "carId_door",
            dataIndex: ["carOwner", "car", "carId"],
            render: (_, { carOwner }) => {
                return carOwner?.car.doorModuleImei ? (
                    <>
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onControlDoor({ carId: carOwner.car.carId, doorType: Common.DOOR_TYPE_DOOR, control: Common.DOOR_CONTROL_OPEN });
                            }}
                        >
                            열기
                        </Button>
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onControlDoor({ carId: carOwner.car.carId, doorType: Common.DOOR_TYPE_DOOR, control: Common.DOOR_CONTROL_CLOSE });
                            }}
                        >
                            잠금
                        </Button>
                    </>
                ) : (
                    String.dash
                );
            },
        },
        {
            align: "center",
            title: "스윙도어",
            dataIndex: ["carOwner", "car", "swingDoorStatus"],
            key: "swingDoorStatus",
            sorter: (a, b, sortOrder) => Utils.sortString(a.carOwner?.car.swingDoorStatus, b.carOwner?.car.swingDoorStatus, sortOrder),
            render: (_, row) => {
                if (row.carOwner?.car?.swingDoorStatus) {
                    switch (row.carOwner?.car?.swingDoorStatus) {
                        case "CLOSED":
                            return "닫힘";
                        case "OPEN":
                            return "열림";
                        case "FAIL_NOT_P":
                            return "기어P오류";
                        case "KEYOFF_NOT_P":
                            return "KeyOff 오류";
                        default:
                            return String.dash;
                    }
                } else return String.dash;
            },
        },
        {
            align: "center",
            title: "스윙도어 컨트롤",
            key: "carId_swingDoor",
            dataIndex: ["carOwner", "car", "carId"],
            render: (_, { carOwner }) => {
                return carOwner?.car.hasSwingDoor ? (
                    <>
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onControlDoor({ carId: carOwner?.car.carId, doorType: Common.DOOR_TYPE_SWING, control: Common.DOOR_CONTROL_OPEN });
                            }}
                        >
                            열기
                        </Button>
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onControlDoor({ carId: carOwner?.car.carId, doorType: Common.DOOR_TYPE_SWING, control: Common.DOOR_CONTROL_CLOSE });
                            }}
                        >
                            잠금
                        </Button>
                    </>
                ) : (
                    String.dash
                );
            },
        },
    ];

    useEffect(() => {
        if (refresh === true) {
            requestGetShareCars();
            setRefresh(false);
        }
    }, [refresh]);

    return (
        <>
            <DataTable hasScroll={false} columns={columns} rowKey="carSharerId" dataSource={shareCars} />
        </>
    );
}

export default RentDigitalKey;
